// W · Working together (QW1_Cowork, film mode QW2_Film). The buddy's place fills the screen (XScene plays the stage's
// loop via loopFor), one slim bar at the bottom. Tap the picture to hide everything but a timer; the controls also
// hide after 7 s. Timer counts from Card.startedAt (server clock). Done -> R, Stuck -> talked through in chat (phone
// v2). Started from chat (params.from 'C'): ×, Done and R come back to the chat, and the server adds the 'done' note
// (doneCard from 'work-chat'); otherwise Today.
import { useEffect, useRef, useState } from 'preact/hooks';
import { C } from '@shared/content/characters';
import { LINES } from '@shared/content/lines';
import { SOUNDS } from '@shared/content/story';
import { useApp, doneCard, startCard } from '../../store';
import { go, replace, useParams } from '../../router';
import { haptic, keepAwake } from '../../native';
import { Screen, XScene, XFace, IconButton, PrimaryButton, SecondaryButton } from '../../components';
import { useReducedMotion } from '../../layout';
import { say } from '../../lib/say';
import { setBetaQuiet } from '../../feedback';
import { serverNow } from './util';
import { stuckOn } from './cards';
import s from './CoworkScreen.module.css';

const clockText = (secs: number) => `${String(Math.floor(secs / 60)).padStart(2, '0')}:${String(secs % 60).padStart(2, '0')}`;
const SOUND_KEY = 'xm.coworkSound';

export function CoworkScreen() {
  const st = useApp((x) => x.state);
  const params = useParams();
  const reduced = useReducedMotion();
  const mounted = useRef(Date.now());
  const [, tick] = useState(0);
  const [li, setLi] = useState(0);
  const [hidden, setHidden] = useState(false);
  const [film, setFilm] = useState(false);
  const [filmHint, setFilmHint] = useState(false);
  const [filmExit, setFilmExit] = useState(false);
  const [busy, setBusy] = useState(false);
  const finishing = useRef(false);
  const [sound, setSound] = useState(() => { try { return localStorage.getItem(SOUND_KEY) !== 'off'; } catch { return true; } });
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const audio = useRef<HTMLAudioElement | null>(null);

  const card = st ? st.plan.cards.find((x) => x.id === params.cur) || null : null;
  const fromChat = params.from === 'C';
  const home = fromChat ? 'C' : 'T';
  const ch = st?.ch || 'margo';
  // The newest working-together sound this buddy has given (route stops), if any has a file.
  const soundFile = st ? [...st.buddy.sounds].reverse().map((id) => SOUNDS[id]?.file).find(Boolean) || null : null;

  const later = (k: string, ms: number, fn: () => void) => { clearTimeout(timers.current[k]); timers.current[k] = setTimeout(fn, ms); };
  const armIdle = () => later('idle', 7000, () => setHidden(true));

  useEffect(() => {
    void keepAwake(true);
    const t = setInterval(() => tick((n) => n + 1), 1000);
    const l = setInterval(() => setLi((n) => n + 1), 9000);
    armIdle();
    return () => { clearInterval(t); clearInterval(l); Object.values(timers.current).forEach(clearTimeout); void keepAwake(false); audio.current?.pause(); };
  }, []);

  // Opened from a notification or the tray: start the card if it isn't running yet. No card: back to Today.
  useEffect(() => {
    if (!st) return;
    if (finishing.current) return;
    if (!card || card.st !== 'todo') { replace(home); return; }
    if (!card.startedAt) void startCard(card.id, params.alt ?? null);
  }, [!!card, card?.st]);

  useEffect(() => {
    if (!soundFile) return;
    if (!audio.current) { audio.current = new Audio(soundFile); audio.current.loop = true; audio.current.volume = 0.5; }
    if (sound) audio.current.play().catch(() => undefined); else audio.current.pause();
    try { localStorage.setItem(SOUND_KEY, sound ? 'on' : 'off'); } catch { /* ignore */ }
  }, [sound, soundFile]);

  // The β tab hides with the controls (and while filming); it comes back when this screen closes.
  const quietBeta = hidden || film;
  useEffect(() => { setBetaQuiet(quietBeta); }, [quietBeta]);
  useEffect(() => () => setBetaQuiet(false), []);

  if (!st || !card) return null;
  const c = C[ch];
  const title = params.alt || card.alt || card.title;
  // Seconds since the card started, in the server's clock. A just-started card (optimistic, device clock) or a
  // clock far off falls back to the time since this screen opened.
  let secs = card.startedAt ? Math.floor((serverNow() - Date.parse(card.startedAt)) / 1000) : -1;
  if (secs < 0 || secs > 12 * 3600) secs = Math.floor((Date.now() - mounted.current) / 1000);
  const clock = clockText(Math.max(0, secs));
  const lines = LINES[ch].cowork;
  const line = say(lines[li % lines.length], { n: c.name, task: card.title });

  const poke = () => armIdle();
  const toggleUi = () => {
    if (film) { setFilmExit(true); setFilmHint(false); later('exit', 3000, () => setFilmExit(false)); return; }
    setHidden((h) => { if (h) poke(); else clearTimeout(timers.current.idle); return !h; });
  };
  const startFilm = () => {
    clearTimeout(timers.current.idle);
    setFilm(true); setFilmHint(true); setFilmExit(false);
    later('hint', 3500, () => setFilmHint(false));
  };
  const exitFilm = () => { setFilm(false); setFilmExit(false); setFilmHint(false); setHidden(false); poke(); };
  const finish = async () => {
    if (busy) return;
    setBusy(true); finishing.current = true;
    const r = await doneCard(card.id, fromChat ? { from: 'work-chat' } : {});
    if (r) { haptic('success'); go('R', { cur: card.id, rbeat: null, from: home }); } else { setBusy(false); finishing.current = false; }
  };
  const ui = !hidden && !film;

  return (
    <Screen theme={ch} id="W" label={`Working on ${title} with ${c.name}`}>
      <XScene ch={ch} stage={st.buddy.stage} mode="clear" />
      <div class={s.topShade} aria-hidden="true" style={{ opacity: ui ? 1 : 0 }} />
      <div class={s.lowShade} aria-hidden="true" style={{ opacity: ui ? 1 : 0 }} />
      <button type="button" class={s.toggle} aria-label={film ? 'Show the exit button' : hidden ? 'Show controls' : 'Hide controls'} onClick={toggleUi} />

      {ui && (
        <div class={s.top}>
          <IconButton icon="close" label="Stop working" size={44} iconSize={18} onClick={() => go(home)} style={{ background: 'rgba(15,14,12,0.55)' }} />
          <div class={s.pill}>
            <XFace ch={ch} e={3} s={28} ring="var(--acc)" alt="" />
            <span>{`${c.name} is working too`}</span>
          </div>
          <div class={s.topRight}>
            <IconButton icon="film" label="Filming mode" size={44} iconSize={20} onClick={startFilm} style={{ background: 'rgba(15,14,12,0.55)' }} />
            {soundFile && (
              <IconButton icon={sound ? 'sound' : 'soundOff'} label={sound ? 'Music on' : 'Music off'} aria-pressed={sound} size={44} iconSize={20}
                onClick={() => { setSound(!sound); poke(); }} style={{ background: 'rgba(15,14,12,0.55)' }} />
            )}
          </div>
        </div>
      )}
      {film && <div class={s.filmClock} role="timer" aria-label={`Time working: ${clock}`}>{clock}</div>}
      {film && filmHint && <p class={s.filmHint} role="status">Filming mode. Start your screen recording. Tap anywhere to get the exit button.</p>}

      <div class={s.spacer} />

      {(ui || film) && (
        <div class={s.subtitle} data-film={film ? 'true' : 'false'} aria-live="polite">
          <p class={s.who}>{c.name}</p>
          <p key={reduced ? 'l' : li} class={s.line}>{line}</p>
        </div>
      )}

      {ui && (
        <div class={s.bar}>
          <div class={s.barTop}>
            <div class={s.now}>
              <span class={s.nowLabel}>NOW</span>
              <span class={s.nowTitle}>{title}</span>
            </div>
            <span class={s.clock} role="timer" aria-label={`Time working: ${clock}`}>{clock}</span>
          </div>
          <div class={s.btns}>
            <PrimaryButton h={50} icon="check" grow={1.35} onClick={() => void finish()} busy={busy} data-cta="done">Done</PrimaryButton>
            <SecondaryButton h={50} icon="lifebuoy" grow={1} onClick={() => stuckOn(card.id)}>Stuck</SecondaryButton>
          </div>
        </div>
      )}
      {hidden && !film && (
        <button type="button" class={s.hiddenPill} aria-label={`Show controls. Time working: ${clock}`} onClick={toggleUi}>
          <span class={s.dot} aria-hidden="true" />
          <span>{clock}</span>
        </button>
      )}
      {film && <span class={s.mark} aria-hidden="true">XIAOMAN</span>}
      {film && filmExit && <button type="button" class={s.exit} onClick={exitFilm}>Exit filming mode</button>}
    </Screen>
  );
}
